import { resolveImagePath } from "@/utils/imageMap";

interface FooterProps {
  className?: string;
}

export function Footer({ className = "" }: FooterProps) {
  return (
    <footer className={`bg-gray-800 text-white py-12 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Company Info Column */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img
                src={resolveImagePath("/images/asi-logo.png")}
                alt="ASI Aircraft Specialists"
                className="h-12 w-auto mb-4 brightness-0 invert"
              />
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Providing the ultimate client experience is our #1 goal. 
              Comprehensive FBO services just 10 minutes north of Louisville, KY.
            </p>
          </div>

          {/* Contact Info Column */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <div className="text-gray-300 text-sm space-y-1">
              <p className="font-medium">Clark Regional Airport (KJVY)</p>
              <p>6005 Propeller Ln</p>
              <p>Sellersburg, IN 47172</p>
              <p className="mt-3">
                <span className="font-medium">TF:</span> (800) 356-3723
              </p>
              <p>
                <span className="font-medium">FBO:</span> (877) JVY-FUEL [589-3835]
              </p>
            </div>
          </div>

          {/* Quick Links Column */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <div className="space-y-2">
              <div>
                <a 
                  href="/fbo" 
                  className="text-gray-300 hover:text-white text-sm transition-colors"
                >
                  FBO
                </a>
              </div>
              <div>
                <a 
                  href="https://pfajets.com/charter/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white text-sm transition-colors"
                >
                  Charter
                </a>
              </div>
              <div>
                <a 
                  href="https://pfajets.com/aircraft-management/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white text-sm transition-colors"
                >
                  Aircraft Management
                </a>
              </div>
              <div>
                <a 
                  href="/maintenance" 
                  className="text-gray-300 hover:text-white text-sm transition-colors"
                >
                  Maintenance & Avionics
                </a>
              </div>
              <div>
                <a 
                  href="/prop" 
                  className="text-gray-300 hover:text-white text-sm transition-colors"
                >
                  Prop Shop
                </a>
              </div>
            </div>
          </div>

          {/* Connect With Us Column */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-4">Connect with Us</h3>
            
            {/* Social Media Links */}
            <div className="flex space-x-3 mb-6">
              <a
                href="https://www.facebook.com/AircraftSpecialistsSvc/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 p-2 rounded transition-colors"
                aria-label="Follow on Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              
              <a
                href="https://www.linkedin.com/company/aircraft-specialists-inc-projets/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-700 hover:bg-blue-800 p-2 rounded transition-colors"
                aria-label="Follow on LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>

            {/* Contact CTA Button */}
            <div className="mb-4">
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-3 text-sm font-medium text-white bg-primary hover:bg-primary-dark transition-colors duration-200 rounded shadow hover:shadow-md"
              >
                CONTACT US TODAY
              </a>
            </div>

            {/* ASRI Information */}
            <div className="text-gray-400 text-sm">
              <p>ASRI 128.9</p>
            </div>
          </div>
        </div>
        
        {/* Copyright Section */}
        <div className="mt-8 pt-8 border-t border-gray-700">
          <div className="text-center text-gray-400 text-sm">
            <p>&copy; 2025 Aircraft Specialists, Inc. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}